import React, { useState, useMemo, useEffect } from "react"
import Breadcrumb from "components/Common/Breadcrumb"
import {
  Card,
  CardBody,
  Table,
  Input,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap"
import Loader from "components/Loader/loader"
import { fetchData } from "utils/Common_api_functions"
import moment from "moment"
import { FaHistory, FaTimes, FaEye } from "react-icons/fa"
import { usePermissionData } from "AppContext"
import PermittedComponent from "PermittedComponent"

const AuditLogs = () => {
  document.title = "Audit Logs | Siddhi Tax Consultant"

  const { checkAccess } = usePermissionData()

  const [pageSize, setPageSize] = useState(20)
  const [loading, setLoading] = useState(false)
  const [logsData, setLogsData] = useState([])

  // Global search
  const [searchString, setSearchString] = useState("")

  // Date range
  const [dateFrom, setDateFrom] = useState("")
  const [dateTo, setDateTo] = useState("")

  const [currentPage, setCurrentPage] = useState(1)
  const [previewLog, setPreviewLog] = useState(null)

  useEffect(() => {
    getLogs()
  }, [])

  async function getLogs() {
    const APIdata = await fetchData(setLoading, "logs")
    if (APIdata?.success) {
      setLogsData(APIdata?.data)
    }
  }

  const clearFilters = () => {
    setSearchString("")
    setDateFrom("")
    setDateTo("")
    setCurrentPage(1)
  }

  const hasActiveFilters = searchString || dateFrom || dateTo

  const filteredData = useMemo(() => {
    return logsData.filter(item => {
      const createdAt = moment(item.createdAt)
      const formattedDate = createdAt
        .format("DD-MM-YYYY - h:mm a")
        .toLowerCase()
      const module = (item.module || "").toLowerCase()
      const action = (item.action || "").toLowerCase()
      const performedBy = (item.staff?.name || "").toLowerCase()
      const search = searchString.toLowerCase()

      // Global search across all columns
      if (
        search &&
        !formattedDate.includes(search) &&
        !module.includes(search) &&
        !action.includes(search) &&
        !performedBy.includes(search)
      ) {
        return false
      }

      // Date range filter
      if (dateFrom && createdAt.isBefore(moment(dateFrom).startOf("day")))
        return false
      if (dateTo && createdAt.isAfter(moment(dateTo).endOf("day"))) return false

      return true
    })
  }, [logsData, searchString, dateFrom, dateTo])

  const totalPages = Math.ceil(filteredData.length / pageSize)
  const start = (currentPage - 1) * pageSize

  const paginatedData = useMemo(
    () => filteredData.slice(start, start + pageSize),
    [filteredData, currentPage, pageSize]
  )

  useEffect(() => {
    setCurrentPage(1)
  }, [searchString, dateFrom, dateTo, pageSize])

  const getActionBadgeColor = (action = "") => {
    const a = action.toLowerCase()
    if (a.includes("delet"))
      return {
        background: "#fff0f0",
        color: "#d33",
        border: "1px solid #ffd0d0",
      }
    if (a.includes("add") || a.includes("creat"))
      return {
        background: "#f0fff4",
        color: "#1a7f37",
        border: "1px solid #c3e6cb",
      }
    if (a.includes("updat") || a.includes("edit"))
      return {
        background: "#fff8e7",
        color: "#856404",
        border: "1px solid #ffd966",
      }
    return {
      background: "#f0f4ff",
      color: "#3085d6",
      border: "1px solid #c7d8f5",
    }
  }

  const parseJSON = str => {
    try {
      return JSON.parse(str)
    } catch {
      return null
    }
  }

  const formatKey = key =>
    key.replace(/([A-Z])/g, " $1").replace(/^./, s => s.toUpperCase())

  const formatValue = val => {
    if (val === null || val === undefined) return "—"
    if (typeof val === "boolean") return val ? "Yes" : "No"
    if (
      typeof val === "string" &&
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(val)
    )
      return moment(val).format("DD-MM-YYYY h:mm a")
    return String(val)
  }

  const getActionType = log => {
    if (log.createdData) return "created"
    if (log.deletedData) return "deleted"
    if (log.previousData || log.updatedData) return "updated"
    return "unknown"
  }

  const getModuleBadgeColor = (module = "") => {
    const palette = {
      customers: { background: "#eef2ff", color: "#556ee6" },
      staff: { background: "#f0fff4", color: "#1a7f37" },
      roles: { background: "#fff8e7", color: "#856404" },
      projects: { background: "#fff0f6", color: "#c72b7a" },
      documents: { background: "#f3f0ff", color: "#6f42c1" },
    }
    const key = Object.keys(palette).find(k => module.toLowerCase().includes(k))
    return key ? palette[key] : { background: "#f5f5f5", color: "#555" }
  }

  return (
    <PermittedComponent isAccess={checkAccess(6, "view") || checkAccess(6, "viewglobal")}>
    <div className="page-content">
      {loading && <Loader />}
      <Breadcrumb title="Dashboard" breadcrumbItem="Audit Logs" />

      <Card className="shadow-lg">
        <CardBody>
          {/* ── Top controls ── */}
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-3">
            {/* Left: page size + search + date range */}
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <Input
                type="select"
                value={pageSize}
                onChange={e => setPageSize(Number(e.target.value))}
                className="rounded-4 w-auto pe-5"
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Input>

              <Input
                className="rounded-4"
                placeholder="Search by date, module, action or staff..."
                value={searchString}
                onChange={e => setSearchString(e.target.value)}
                style={{ width: 300 }}
              />

              {/* Date range */}
              <div className="d-flex align-items-center gap-2">
                <label
                  className="mb-0 text-muted"
                  style={{ fontSize: 13, whiteSpace: "nowrap" }}
                >
                  From
                </label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={e => setDateFrom(e.target.value)}
                  className="rounded-4"
                  style={{ width: 155 }}
                />
                <label
                  className="mb-0 text-muted"
                  style={{ fontSize: 13, whiteSpace: "nowrap" }}
                >
                  To
                </label>
                <Input
                  type="date"
                  value={dateTo}
                  min={dateFrom || undefined}
                  onChange={e => setDateTo(e.target.value)}
                  className="rounded-4"
                  style={{ width: 155 }}
                />
              </div>

              {hasActiveFilters && (
                <Button
                  size="sm"
                  color="secondary"
                  className="rounded-4 d-flex align-items-center gap-1"
                  onClick={clearFilters}
                  title="Clear all filters"
                >
                  <FaTimes size={11} /> Clear
                </Button>
              )}
            </div>

            {/* Right: total count badge */}
            <div className="d-flex align-items-center gap-2">
              <FaHistory size={18} className="text-muted" />
              <span className="badge bg-blue fs-6 px-3">
                {filteredData.length}{" "}
                {filteredData.length === 1 ? "Log" : "Logs"}
              </span>
            </div>
          </div>

          {/* ── Table ── */}
          <Table bordered className="mb-0">
            <thead className="table-dark">
              <tr>
                <th style={{ width: 50 }}>#</th>
                <th style={{ minWidth: 120 }}>Date &amp; Time</th>
                <th style={{ minWidth: 120 }}>Module</th>
                <th style={{ width: 550 }}>Action</th>
                <th style={{ minWidth: 140 }}>Performed By</th>
                <th style={{ width: 90 }}>Preview</th>
              </tr>
            </thead>

            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-4 text-muted">
                    {hasActiveFilters
                      ? "No logs match the current filters."
                      : "No audit logs found."}
                  </td>
                </tr>
              ) : (
                paginatedData.map((item, index) => (
                  <tr key={item.id}>
                    <td className="text-muted" style={{ fontSize: 13 }}>
                      {start + index + 1}
                    </td>
                    <td style={{ fontSize: 13, whiteSpace: "nowrap" }}>
                      <div className="fw-bold">
                        {moment(item.createdAt).format("DD-MM-YYYY")}
                      </div>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        {moment(item.createdAt).format("h:mm a")}
                      </div>
                    </td>
                    <td>
                      <span
                        className="badge rounded-2 px-2 py-1"
                        style={{ fontSize: 12, color: "black" }}
                      >
                        {item.module || "—"}
                      </span>
                    </td>
                    <td style={{ fontSize: 13 }}>
                      <span
                        className="badge rounded-2 px-2 py-1"
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          color: "black",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {item.action || "—"}
                      </span>
                    </td>
                    <td style={{ fontSize: 13 }}>
                      <div className="fw-bold">{item.staff?.name || "—"}</div>
                      {item.staffId && (
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          ID: #{item.staffId}
                        </div>
                      )}
                    </td>
                    <td className="text-center">
                      <Button
                        size="sm"
                        color="primary"
                        outline
                        className="rounded-3 d-inline-flex align-items-center gap-1"
                        onClick={() => setPreviewLog(item)}
                        title="Preview log"
                      >
                        <FaEye size={13} /> View
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {/* ── Pagination ── */}
          <nav
            aria-label="Page navigation"
            className="d-flex justify-content-between align-items-center mt-3"
          >
            <h5 className="mb-0">
              Showing {filteredData.length === 0 ? 0 : start + 1} to{" "}
              {Math.min(start + pageSize, filteredData.length)} of{" "}
              {logsData.length} entries
              {hasActiveFilters && filteredData.length !== logsData.length && (
                <span className="text-muted ms-1" style={{ fontSize: 13 }}>
                  (filtered from {logsData.length} total)
                </span>
              )}
            </h5>
            <ul className="pagination mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                >
                  Previous
                </button>
              </li>

              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1
                if (
                  totalPages <= 7 ||
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)
                ) {
                  return (
                    <li
                      key={pageNum}
                      className={`page-item ${
                        pageNum === currentPage ? "active" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    </li>
                  )
                }
                if (
                  pageNum === currentPage - 3 ||
                  pageNum === currentPage + 3
                ) {
                  return (
                    <li key={pageNum} className="page-item disabled">
                      <span className="page-link">…</span>
                    </li>
                  )
                }
                return null
              })}

              <li
                className={`page-item ${
                  currentPage === totalPages || totalPages === 0
                    ? "disabled"
                    : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() =>
                    setCurrentPage(prev => Math.min(prev + 1, totalPages))
                  }
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </CardBody>
      </Card>
      {/* ── Preview Modal ── */}
      {previewLog && (() => {
          const type = getActionType(previewLog)
          const badgeStyle =
            type === "created"
              ? {
                  background: "#f0fff4",
                  color: "#1a7f37",
                  border: "1px solid #c3e6cb",
                }
              : type === "deleted"
              ? {
                  background: "#fff0f0",
                  color: "#d33",
                  border: "1px solid #ffd0d0",
                }
              : type === "updated"
              ? {
                  background: "#fff8e7",
                  color: "#856404",
                  border: "1px solid #ffd966",
                }
              : { background: "#f5f5f5", color: "#555" }

          const renderTable = (data, title, style) => {
            if (!data) return null
            const entries = Object.entries(data)
            return (
              <div className="mb-3">
                {title && (
                  <div
                    className="fw-semibold px-2 py-1 rounded-2 mb-2 d-inline-block"
                    style={{ fontSize: 12, ...style }}
                  >
                    {title}
                  </div>
                )}
                <Table
                  bordered
                  size="sm"
                  className="mb-0"
                  style={{ fontSize: 13 }}
                >
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "40%" }}>Field</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map(([key, val]) => (
                      <tr key={key}>
                        <td className="fw-medium text-muted">
                          {formatKey(key)}
                        </td>
                        <td style={{ wordBreak: "break-word" }}>
                          {formatValue(val)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )
          }

          const renderRolesPermissionsDiff = () => {
            const prevRaw = parseJSON(previewLog.previousData) || {}
            const nextRaw = parseJSON(previewLog.updatedData) || {}
            const prevPerms = parseJSON(prevRaw.permissions) || []
            const nextPerms = parseJSON(nextRaw.permissions) || []

            const prevMap = {}
            prevPerms.forEach(p => { prevMap[p.id] = p })
            const nextMap = {}
            nextPerms.forEach(p => { nextMap[p.id] = p })

            const allIds = Array.from(
              new Set([...prevPerms.map(p => p.id), ...nextPerms.map(p => p.id)])
            )

            const permLabelMap = {
              view: "View",
              viewglobal: "View Global",
              add: "Add",
              edit: "Edit",
              delete: "Delete",
              isViewglobal: "Can View Global",
              isAdd: "Can Add",
              isEdit: "Can Edit",
              isDelete: "Can Delete",
            }

            const formatPermVal = val => {
              if (val === true || val === 1) return "Enabled"
              if (val === false || val === 0) return "Disabled"
              return String(val)
            }

            const changes = []
            allIds.forEach(id => {
              const prev = prevMap[id] || {}
              const next = nextMap[id] || {}
              const moduleName = next.module || prev.module || `ID ${id}`
              Object.keys(permLabelMap).forEach(field => {
                const prevVal = prev[field]
                const nextVal = next[field]
                if (String(prevVal) !== String(nextVal)) {
                  changes.push({ module: moduleName, field, prevVal, nextVal })
                }
              })
            })

            if (changes.length === 0) {
              return <p className="text-muted">No permission changes detected.</p>
            }

            return (
              <Table bordered size="sm" style={{ fontSize: 13 }}>
                <thead className="table-light">
                  <tr>
                    <th>Module</th>
                    <th>Permission</th>
                    <th>Before</th>
                    <th>After</th>
                  </tr>
                </thead>
                <tbody>
                  {changes.map((c, i) => (
                    <tr key={i} style={{ background: "#fffbe6" }}>
                      <td className="fw-medium">{c.module}</td>
                      <td className="text-muted">{permLabelMap[c.field]}</td>
                      <td style={{ color: "#d33" }}>{formatPermVal(c.prevVal)}</td>
                      <td style={{ color: "#1a7f37" }}>{formatPermVal(c.nextVal)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )
          }

          const renderContent = () => {
            if (type === "created") {
              const data = parseJSON(previewLog.createdData)
              return renderTable(data, null, {})
            }
            if (type === "deleted") {
              const data = parseJSON(previewLog.deletedData)
              return renderTable(data, null, {})
            }
            if (type === "updated" && previewLog.module === "Roles & Permissions") {
              return renderRolesPermissionsDiff()
            }
            if (type === "updated") {
              const prev = parseJSON(previewLog.previousData) || {}
              const next = parseJSON(previewLog.updatedData) || {}
              const keys = Array.from(
                new Set([...Object.keys(prev), ...Object.keys(next)])
              )
              return (
                <Table bordered size="sm" style={{ fontSize: 13 }}>
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "35%" }}>Field</th>
                      <th>Before</th>
                      <th>After</th>
                    </tr>
                  </thead>
                  <tbody>
                    {keys.map(key => {
                      const changed =
                        formatValue(prev[key]) !== formatValue(next[key])
                      return (
                        <tr
                          key={key}
                          style={changed ? { background: "#fffbe6" } : {}}
                        >
                          <td className="fw-medium text-muted">
                            {formatKey(key)}
                          </td>
                          <td
                            style={{ color: "#d33", wordBreak: "break-word" }}
                          >
                            {formatValue(prev[key])}
                          </td>
                          <td
                            style={{
                              color: "#1a7f37",
                              wordBreak: "break-word",
                            }}
                          >
                            {formatValue(next[key])}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
              )
            }
            return <p className="text-muted">No detail data available.</p>
          }

          return (
            <Modal
              isOpen
              toggle={() => setPreviewLog(null)}
              size="lg"
              centered
              scrollable
            >
              <ModalHeader toggle={() => setPreviewLog(null)}>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <span>Log Preview</span>
                  <span
                    className="badge rounded-2 px-2 py-1"
                    style={{ fontSize: 12, fontWeight: 500, ...badgeStyle }}
                  >
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </span>
                  <span
                    className="text-muted"
                    style={{ fontSize: 13, fontWeight: 400 }}
                  >
                    {previewLog.module}
                  </span>
                </div>
              </ModalHeader>
              <ModalBody>
                <p className="text-muted mb-3" style={{ fontSize: 13 }}>
                  {previewLog.action}
                </p>
                {renderContent()}
                <div className="text-muted mt-3" style={{ fontSize: 12 }}>
                  <span className="me-3">
                    Performed by:{" "}
                    <strong>{previewLog.staff?.name || "—"}</strong>
                  </span>
                  <span>
                    {moment(previewLog.createdAt).format("DD-MM-YYYY h:mm a")}
                  </span>
                </div>
              </ModalBody>
            </Modal>
          )
        })()}
    </div>
    </PermittedComponent>
  )
}

export default AuditLogs
